import {
  SESSION_LOGIN_NAME_KEY,
  SESSION_USER_COUNTRY_CODE_KEY,
  SESSION_USER_MOBILE_NO_KEY,
  SESSION_USER_NAME_KEY,
  SUBSCRIPTION_TIER_LARGE,
  SUBSCRIPTION_TIER_MID,
  SUBSCRIPTION_TIER_PRICING,
  SUBSCRIPTION_TIER_SMALL,
} from "./VMConstants";

export const currencyToCountryKey = (currencyCode) => {
  if (!currencyCode) return "IN";
  const cc = String(currencyCode).toUpperCase();
  if (cc === "INR") return "IN";
  if (cc === "USD") return "US";
  return "US";
};

export const getCurrencySymbol = (currencyCode) => {
  const cc = String(currencyCode || "INR").toUpperCase();
  if (cc === "INR") return "₹";
  if (cc === "USD") return "$";
  return "$";
};

export const ANNUAL_DISCOUNT_PERCENT = 15;

export const formatTierAmount = (amount, currencyCode = "INR") => {
  if (amount == null) return "";
  const codeOrSymbol = String(currencyCode || "INR").toUpperCase();
  const locale =
    codeOrSymbol === "INR" || currencyCode === "₹" ? "en-IN" : "en-US";
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    Number(amount)
  );
};

export const normalizeCheckoutCurrency = (currencyCode = "INR") => {
  const raw = String(currencyCode || "INR").trim();
  if (raw === "₹" || raw.toUpperCase() === "INR") return "INR";
  if (raw === "$" || raw.toUpperCase() === "USD") return "USD";
  return raw.toUpperCase() || "INR";
};

/** Professional currency display, e.g. ₹5,100 or $100 */
export const formatTierCurrency = (amount, currencyCode = "INR") => {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  const code = normalizeCheckoutCurrency(currencyCode);
  const locale = code === "INR" ? "en-IN" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code === "INR" || code === "USD" ? code : "USD",
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

export const SUBSCRIPTION_GST_PERCENT = 18;
export const SUBSCRIPTION_GST_FOOTNOTE =
  "Exclusive of 18% tax (GST) · Secure payment via Razorpay";

/** Always apply — all countries / currencies. */
export const shouldApplySubscriptionGst = () => true;

export function roundCheckoutAmount(value, currency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const c = normalizeCheckoutCurrency(currency);
  if (c === "INR") return Math.round(n);
  return Math.round(n * 100) / 100;
}

/** Checkout amounts: INR whole rupees, other currencies 2 decimal places. */
export const formatCheckoutCurrency = (amount, currencyCode = "INR") => {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  const code = normalizeCheckoutCurrency(currencyCode);
  const locale = code === "INR" ? "en-IN" : "en-US";
  const isInr = code === "INR";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code === "INR" || code === "USD" ? code : "USD",
    minimumFractionDigits: isInr ? 0 : 2,
    maximumFractionDigits: isInr ? 0 : 2,
  }).format(Number(amount));
};

export function getSubscriptionGstBreakdown(
  baseAmount,
  opts = {}
) {
  const base = Number(baseAmount);
  const rate = opts?.percent ?? SUBSCRIPTION_GST_PERCENT;
  if (!Number.isFinite(base) || base < 0) return null;
  if (!Number.isFinite(rate) || rate < 0) return null;

  const currency = normalizeCheckoutCurrency(opts?.currency);
  const gstAmount = roundCheckoutAmount((base * rate) / 100, currency);
  const normalizedBase = roundCheckoutAmount(base, currency);
  return {
    applyGst: true,
    baseAmount: normalizedBase,
    gstPercent: rate,
    gstAmount,
    totalAmount: roundCheckoutAmount(normalizedBase + gstAmount, currency),
    currency,
  };
}

export const buildCreateOrderTierGstPayload = (breakdown) => {
  if (!breakdown) return {};
  return {
    apply_gst: true,
    gst_percent: breakdown.gstPercent,
    base_amount: breakdown.baseAmount,
    gst_amount: breakdown.gstAmount,
    total_amount: breakdown.totalAmount,
    amount: breakdown.totalAmount,
  };
};

/**
 * Prefer GST fields from subscriptionPlans. Fallback client math only if catalog
 * has no gst_amount / total_amount (old API).
 */
export function getPriceForCheckout(planRef, opts = {}) {
  if (!planRef) return null;

  const currency = normalizeCheckoutCurrency(
    planRef.currency || opts.currency || "INR"
  );
  const baseAmount = Number(
    planRef.amount ?? planRef.priceEntry?.amount ?? planRef.price
  );
  const gstAmount = planRef.gstAmount ?? planRef.priceEntry?.gst_amount;
  const totalAmount = planRef.totalAmount ?? planRef.priceEntry?.total_amount;
  const gstPercent =
    planRef.gstPercent ??
    planRef.priceEntry?.gst_percent ??
    planRef.tax?.gst_percent ??
    SUBSCRIPTION_GST_PERCENT;
  const taxLabel = planRef.tax?.label || opts.taxLabel || `Tax / GST (${gstPercent}%)`;
  const exclusiveNote =
    planRef.exclusiveNote ||
    planRef.tax?.exclusive_note ||
    opts.exclusiveNote ||
    SUBSCRIPTION_GST_FOOTNOTE;

  if (
    gstAmount != null &&
    totalAmount != null &&
    Number.isFinite(baseAmount) &&
    baseAmount >= 0
  ) {
    return {
      applyGst: true,
      fromApi: true,
      baseAmount: Number(baseAmount),
      gstPercent: Number(gstPercent),
      gstAmount: Number(gstAmount),
      totalAmount: Number(totalAmount),
      totalAmountMinor:
        planRef.totalAmountMinor != null
          ? Number(planRef.totalAmountMinor)
          : planRef.priceEntry?.total_amount_minor != null
            ? Number(planRef.priceEntry.total_amount_minor)
            : undefined,
      currency,
      taxLabel,
      exclusiveNote,
    };
  }

  const fallback = getSubscriptionGstBreakdown(baseAmount, {
    percent: Number(gstPercent),
    currency,
  });
  if (!fallback) return null;

  return {
    ...fallback,
    fromApi: false,
    taxLabel,
    exclusiveNote,
  };
}

/**
 * Card exclusive price = API sale amount for the selected period.
 * Yearly strikethrough is monthly × 12 when that is higher than the sale price.
 */
export const getTierBillingDisplay = ({ billingCycle, planRef, monthlyPlan }) => {
  const currency = planRef?.currency || monthlyPlan?.currency || "INR";

  if (billingCycle === "Monthly") {
    if (planRef?.amount == null) return null;
    return {
      amount: Number(planRef.amount),
      listAmount: null,
      currency,
      period: "month",
      discountPercent: 0,
    };
  }

  if (planRef?.amount == null) return null;

  const saleAmount = Number(planRef.amount);
  const listAmount =
    monthlyPlan?.amount != null ? Number(monthlyPlan.amount) * 12 : null;
  const showList = listAmount != null && listAmount > saleAmount;
  const discountPercent = showList
    ? Math.round(((listAmount - saleAmount) / listAmount) * 100)
    : 0;

  return {
    amount: saleAmount,
    listAmount: showList ? listAmount : null,
    currency,
    period: "year",
    discountPercent,
  };
};

export const isTierSubscriptionActive = (statusData) => {
  if (!statusData || statusData.status_code !== 200) return false;
  return Boolean(
    statusData.is_paid ??
      statusData.is_subscription_active ??
      statusData.is_licence_active
  );
};

/** Expiry from checkUserSubscriptionStatusTier response */
export const getSubscriptionStatusExpiryDate = (statusData) => {
  if (!statusData) return null;
  return (
    statusData.license_expires_at ??
    statusData.tier_usage?.trial_ends_at ??
    statusData.trial_ends_at ??
    statusData.tier_subscription?.end_date ??
    statusData.end_date ??
    statusData.expires_at ??
    statusData.expiry_date ??
    statusData.subscription_end_date ??
    null
  );
};

export const isSubscriptionLicenseValid = (statusData) => {
  if (!statusData || statusData.status_code !== 200) return false;
  if (statusData.is_licence_active === false) return false;

  const expiryDate = getSubscriptionStatusExpiryDate(statusData);
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    if (!Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now()) {
      return false;
    }
  }

  return isTierSubscriptionActive(statusData);
};

/** Dashboard exports — trial and premium allowed; free (post-trial) blocked */
export const canAccessDashboardPremiumFeatures = (statusData) => {
  if (!statusData || statusData.status_code !== 200) return false;
  const plan = String(statusData.plan || "").toLowerCase();
  if (plan === "free") return false;
  if (plan === "trial") return statusData.is_licence_active !== false;
  if (plan === "premium") return isSubscriptionLicenseValid(statusData);
  return false;
};

export const isTierDetailsActive = (details) => {
  if (!details || details.status_code !== 200) return false;
  return Boolean(
    details.is_paid ?? details.is_licence_active ?? details.tier_subscription
  );
};

/** Resolve subscription end date from tier details API shapes */
export const getTierSubscriptionExpiryDate = (details) => {
  if (!details) return null;
  return (
    details.tier_subscription?.end_date ??
    details.end_date ??
    details.subscription_end_date ??
    details.tier_subscription?.subscription_end_date ??
    null
  );
};

export const formatSubscriptionExpiryDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const getDaysUntilSubscriptionExpiry = (value) => {
  if (!value) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
};

/** e.g. "Expires today", "Expires in 1 day", "Expires in 29 days" */
export const formatExpiresInDaysLabel = (value) => {
  const days = getDaysUntilSubscriptionExpiry(value);
  if (days == null) return "";
  if (days < 0) return "Expired";
  if (days === 0) return "Expires today";
  return `Expires in ${days} day${days === 1 ? "" : "s"}`;
};

/** e.g. "0 days remaining", "2 days remaining", "3 months 2 days remaining" */
export const formatSubscriptionRemainingLabel = (value) => {
  const totalDays = getDaysUntilSubscriptionExpiry(value);
  if (totalDays == null) return "";
  if (totalDays < 0) return "expired";
  if (totalDays === 0) return "0 days remaining";

  const end = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  let months =
    (end.getFullYear() - today.getFullYear()) * 12 +
    (end.getMonth() - today.getMonth());
  const afterMonths = new Date(today);
  afterMonths.setMonth(afterMonths.getMonth() + months);
  let days = Math.round((end - afterMonths) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    months -= 1;
    afterMonths.setMonth(afterMonths.getMonth() - 1);
    days = Math.round((end - afterMonths) / (1000 * 60 * 60 * 24));
  }

  const parts = [];
  if (months > 0) {
    parts.push(`${months} month${months === 1 ? "" : "s"}`);
  }
  if (days > 0 || months === 0) {
    parts.push(`${days} day${days === 1 ? "" : "s"}`);
  }
  return `${parts.join(" ")} remaining`;
};

/** e.g. "Expires on August 12, 2026 (2 days remaining)" */
export const formatExpiresOnWithRemaining = (value) => {
  const dateLabel = formatSubscriptionExpiryDate(value);
  if (!dateLabel) return "";
  const remaining = formatSubscriptionRemainingLabel(value);
  if (!remaining) return `Expires on ${dateLabel}`;
  return `Expires on ${dateLabel} (${remaining})`;
};

/** Expiry from team-limit or subscription-status payloads */
export const getTrialOrSubscriptionExpiryDate = (statusData, limitData) => {
  return (
    getSubscriptionStatusExpiryDate(statusData) ??
    limitData?.license_expires_at ??
    limitData?.usage?.trial_ends_at ??
    limitData?.trial_ends_at ??
    limitData?.end_date ??
    null
  );
};

export const TIER_ORDER = [
  SUBSCRIPTION_TIER_SMALL,
  SUBSCRIPTION_TIER_MID,
  SUBSCRIPTION_TIER_LARGE,
];

/** Same feature set across every subscription plan */
export const TIER_COMMON_FEATURES = [
  "Unlimited Venues & Sub-Venues",
  "Enquiry Management",
  "Venue Availability Check",
  "Followups & Appointments",
  "Event Vendor Management",
  "Invoice Generation",
  "Payment Reminders",
  "Team & Role Management",
  "Visitors Log",
  "Task Management",
  "Checklist Management",
  "Feedback Management",
  "Revenue Checker",
];

export const TIER_UI_CONFIG = {
  [SUBSCRIPTION_TIER_SMALL]: {
    accent: "#34657F",
    lightBg: "#E8F1F4",
    idealFor:
      "Ideal for Farm Houses, Villas , Private Event Spaces",
    features: TIER_COMMON_FEATURES,
    recommendedTeam: "1 Admin, 1 Manager, 1 Security Guard",
    comparisonLabel: "Small Venues Plan",
  },
  [SUBSCRIPTION_TIER_MID]: {
    accent: "#1E3A8A",
    lightBg: "#EEF2FF",
    idealFor:
      "Ideal for Banquet Halls, Small Convention Halls, Small Function Halls & Small Resorts",
    features: TIER_COMMON_FEATURES,
    recommendedTeam:
      "1 Admin, 2 Managers, 2 Executives, 1 Receptionist, 2 Security Guards",
    comparisonLabel: "Mid-Size Venues Plan",
  },
  [SUBSCRIPTION_TIER_LARGE]: {
    accent: "#5B21B6",
    lightBg: "#F5F3FF",
    idealFor:
      "Ideal for Big Convention Halls, Big Function Halls & Big Resorts and Other Large Event Venues",
    features: TIER_COMMON_FEATURES,
    recommendedTeam: "Unlimited users & venues with role-based access",
    recommendedTeamLabel: "User Access:",
    comparisonLabel: "Large-Size Venues Plan",
    isEnterprise: true,
  },
};

export const COMPARISON_FEATURES = [
  { name: "Lead Management", small: true, mid: true, enterprise: true },
  { name: "Booking Calendar", small: true, mid: true, enterprise: true },
  { name: "Customer CRM", small: true, mid: true, enterprise: true },
  { name: "Reports & Analytics", small: true, mid: true, enterprise: true },
  { name: "Multi-Venue Management", small: true, mid: true, enterprise: true },
  { name: "API Access", small: false, mid: false, enterprise: true },
  { name: "White Label", small: false, mid: false, enterprise: true },
];

export const VENUE_TYPE_FOOTER = [
  { icon: "🏛️", label: "Convention Centers" },
  { icon: "🎉", label: "Banquet Halls" },
  { icon: "🏢", label: "Function Halls" },
  { icon: "🏨", label: "Resorts" },
  { icon: "🌾", label: "Farm Houses" },
  { icon: "🏠", label: "Club Houses" },
  { icon: "💒", label: "Wedding Venues" },
  { icon: "🎪", label: "Event Spaces" },
];

export const getTierPricingConfig = (countryKey = "IN") => {
  return SUBSCRIPTION_TIER_PRICING[countryKey] || SUBSCRIPTION_TIER_PRICING.IN;
};

export const getDefaultTierPlans = (countryKey = "IN") => {
  const cfg = getTierPricingConfig(countryKey);
  const plans = [];

  TIER_ORDER.forEach((tier) => {
    const tierCfg = cfg.tiers[tier];
    if (!tierCfg) return;

    Object.entries(tierCfg.plans).forEach(([planName, amount]) => {
      plans.push({
        tier,
        displayName: tierCfg.displayName,
        planName,
        amount,
        currency: cfg.currency,
        maxUsers: tierCfg.maxUsers ?? null,
        licencesIncluded: tierCfg.licencesIncluded ?? 1,
        unlimitedUsers: Boolean(tierCfg.unlimitedUsers),
        isMostPopular: Boolean(tierCfg.isMostPopular),
        isEnterprise: Boolean(tierCfg.isEnterprise),
      });
    });
  });

  return plans;
};

export const normalizeTierPlans = (apiData) => {
  const rawPlans = apiData?.plans ?? apiData?.data ?? [];
  if (!Array.isArray(rawPlans)) return [];

  const plans = [];

  rawPlans.forEach((plan) => {
    const tier = plan.plan_tier || plan.tier || plan.name;
    const currency = plan.currency || apiData?.currency || "INR";
    const displayName = plan.display_name || plan.displayName;
    const prices = plan.prices;

    if (prices && typeof prices === "object") {
      Object.entries(prices).forEach(([planName, priceObj]) => {
        plans.push({
          tier,
          displayName,
          planName,
          amount: priceObj?.amount ?? 0,
          amountMinor: priceObj?.amount_minor,
          gstPercent: priceObj?.gst_percent,
          gstAmount: priceObj?.gst_amount,
          totalAmount: priceObj?.total_amount,
          totalAmountMinor: priceObj?.total_amount_minor,
          currency,
          symbol: plan.symbol || apiData?.symbol,
          tax: plan.tax || apiData?.tax || null,
          exclusiveNote:
            plan.tax?.exclusive_note ||
            apiData?.tax?.exclusive_note ||
            null,
          priceEntry: priceObj,
          maxUsers: plan.max_users ?? null,
          licencesIncluded: plan.licence_count ?? 1,
          unlimitedUsers: Boolean(plan.unlimited_users),
          isMostPopular: Boolean(plan.is_most_popular),
          isEnterprise: tier === SUBSCRIPTION_TIER_LARGE,
          raw: plan,
        });
      });
      return;
    }

    plans.push({
      tier,
      displayName,
      planName: plan.plan_name || plan.billing_cycle || plan.duration,
      amount: plan.amount ?? plan.price ?? plan.plan_amount ?? 0,
      currency,
      maxUsers: plan.max_users ?? plan.user_limit ?? null,
      licencesIncluded: plan.licences_included ?? plan.licence_count ?? 1,
      unlimitedUsers: Boolean(plan.unlimited_users),
      isMostPopular: Boolean(plan.is_most_popular),
      isEnterprise: tier === SUBSCRIPTION_TIER_LARGE,
      raw: plan,
    });
  });

  return plans.filter((p) => p.tier && p.planName);
};

/** Prefer API amounts when present; fall back to product pricing config */
export const resolveTierPlans = (apiData, countryKey = "IN") => {
  const fromApi = normalizeTierPlans(apiData);
  if (fromApi.length) return fromApi;

  return getDefaultTierPlans(countryKey);
};

export const groupPlansByTier = (plans) => {
  const grouped = {};
  plans.forEach((plan) => {
    if (!plan.tier) return;
    if (!grouped[plan.tier]) grouped[plan.tier] = [];
    grouped[plan.tier].push(plan);
  });
  return grouped;
};

export const getTierUserLimitLabel = (planOrTierCfg) => {
  if (!planOrTierCfg) return "";
  if (planOrTierCfg.unlimitedUsers) return "Unlimited Users";
  if (planOrTierCfg.maxUsers != null) {
    return `Up to ${planOrTierCfg.maxUsers} Users`;
  }
  return "";
};

export const getTierDisplayName = (tier, planRef, countryKey = "IN") => {
  if (planRef?.displayName) return planRef.displayName;
  const cfg = getTierPricingConfig(countryKey).tiers[tier];
  return cfg?.displayName || tier;
};

export const getTierDisplayMeta = (tier, countryKey = "IN") => {
  const tierCfg = getTierPricingConfig(countryKey).tiers[tier] || {};
  const ui = TIER_UI_CONFIG[tier] || {};

  return {
    accent: ui.accent || "#607d8b",
    lightBg: ui.lightBg || "#f5f5f5",
    displayName: tierCfg.displayName || ui.comparisonLabel || tier,
    idealFor: ui.idealFor || "",
    features: ui.features || [],
    recommendedTeam: ui.recommendedTeam,
    recommendedTeamLabel: ui.recommendedTeamLabel || "Recommended Team:",
    comparisonLabel: ui.comparisonLabel || tierCfg.displayName || tier,
    isEnterprise: Boolean(tierCfg.isEnterprise || ui.isEnterprise),
    isMostPopular: Boolean(tierCfg.isMostPopular),
    userLimitLabel: getTierUserLimitLabel(tierCfg),
  };
};

const firstPrefixedId = (candidates, prefix) =>
  candidates.find((id) => typeof id === "string" && id.startsWith(prefix)) || null;

/** Razorpay checkout contact/name/email from the logged-in web session. */
export const getSessionRazorpayPrefill = (sessionFactory) => {
  const name = String(sessionFactory?.get(SESSION_USER_NAME_KEY) || "").trim();
  const loginName = String(sessionFactory?.get(SESSION_LOGIN_NAME_KEY) || "").trim();
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginName) ? loginName : "";

  const mobileDigits = String(sessionFactory?.get(SESSION_USER_MOBILE_NO_KEY) ?? "").replace(
    /\D/g,
    ""
  );
  const countryDigits =
    String(sessionFactory?.get(SESSION_USER_COUNTRY_CODE_KEY) ?? "91").replace(/\D/g, "") ||
    "91";

  let contact = "";
  if (mobileDigits) {
    contact = mobileDigits.startsWith(countryDigits)
      ? `+${mobileDigits}`
      : `+${countryDigits}${mobileDigits}`;
  }

  return { name, email, contact };
};

const pickInternalSubscriptionId = (...candidates) => {
  for (const value of candidates) {
    if (value == null || value === "") continue;
    if (typeof value === "string" && value.startsWith("sub_")) continue;
    return value;
  }
  return null;
};

export const extractOrderPayload = (orderResponse) => {
  const nestedOrder = orderResponse?.order ?? orderResponse?.data?.order ?? null;
  const order = nestedOrder || orderResponse;
  const checkout =
    orderResponse?.checkout ||
    orderResponse?.data?.checkout ||
    order?.checkout ||
    null;

  const subscriptionId = pickInternalSubscriptionId(
    orderResponse?.subscriptionId,
    orderResponse?.data?.subscriptionId,
    nestedOrder?.subscriptionId,
    orderResponse?.subscription_id,
    orderResponse?.data?.subscription_id
  );

  const razorpaySubscriptionId = firstPrefixedId(
    [
      checkout?.subscription_id,
      orderResponse?.subscription_id,
      orderResponse?.razorpay_subscription_id,
      orderResponse?.data?.subscription_id,
      orderResponse?.data?.razorpay_subscription_id,
      order?.subscription_id,
      order?.razorpay_subscription_id,
    ],
    "sub_"
  );

  const customerId = firstPrefixedId(
    [
      checkout?.customer_id,
      orderResponse?.customer_id,
      orderResponse?.data?.customer_id,
      order?.customer_id,
    ],
    "cust_"
  );

  return {
    order: {
      id: orderResponse?.id ?? order?.id ?? order?.order_id,
      amount: orderResponse?.amount ?? order?.amount,
      currency: orderResponse?.currency ?? order?.currency,
      key: orderResponse?.key ?? order?.key ?? orderResponse?.razorpay_key,
      recurring: Boolean(
        orderResponse?.recurring ?? checkout?.recurring ?? razorpaySubscriptionId
      ),
    },
    subscriptionId,
    razorpaySubscriptionId,
    customerId,
    checkout,
    prefill:
      orderResponse?.prefill ||
      orderResponse?.data?.prefill ||
      checkout?.prefill ||
      order?.prefill ||
      null,
  };
};

/**
 * Autopay Checkout: prefer `checkout`. If subscription_id exists, do not open
 * with order_id (mandate vs one-time).
 */
export const buildRazorpayAutopayOptions = ({
  order,
  checkout,
  sessionPrefill,
  orderPrefill,
}) => {
  const checkoutObj =
    checkout && typeof checkout === "object" && !Array.isArray(checkout)
      ? { ...checkout }
      : {};

  const options = {
    ...checkoutObj,
    key: checkoutObj.key || order?.key,
    amount: order?.amount ?? checkoutObj.amount,
    currency: order?.currency || checkoutObj.currency,
  };

  const subscriptionId = checkoutObj.subscription_id || order?.subscription_id;
  const customerId = checkoutObj.customer_id || order?.customer_id;

  if (subscriptionId) {
    options.subscription_id = subscriptionId;
    if (customerId) options.customer_id = customerId;
    options.recurring = "1";
    delete options.order_id;
  } else if (order?.id) {
    options.order_id = order.id;
    delete options.subscription_id;
    delete options.recurring;
  }

  const serverPrefill = {
    ...(orderPrefill || {}),
    ...(checkoutObj.prefill || {}),
  };
  options.prefill = {
    name: serverPrefill.name || sessionPrefill?.name || "",
    email: serverPrefill.email || sessionPrefill?.email || "",
    contact: serverPrefill.contact || sessionPrefill?.contact || "",
  };

  return options;
};

export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export const computeAnnualSavingsPercent = (monthlyPlan, yearlyPlan) => {
  if (!monthlyPlan?.amount || !yearlyPlan?.amount) return ANNUAL_DISCOUNT_PERCENT;
  const fullYearMonthly = monthlyPlan.amount * 12;
  if (fullYearMonthly <= 0) return ANNUAL_DISCOUNT_PERCENT;
  return Math.round(
    ((fullYearMonthly - yearlyPlan.amount) / fullYearMonthly) * 100
  ) || ANNUAL_DISCOUNT_PERCENT;
};
